/**
 * Minimal native-DOM replacements for the jQuery patterns this app relied on
 * (SD-13404 phase 4). Kept intentionally small - only the behaviors actually
 * used elsewhere in ClientApp.
 */

/**
 * Matches jQuery's `:visible` semantics: an element counts as visible if it
 * has rendered geometry, regardless of what's making it invisible (display:none,
 * detached from the document, zero size, etc).
 */
export function isVisible(el: Element | null | undefined): boolean {
	if (!el) return false;
	const htmlEl = el as HTMLElement;
	return !!(htmlEl.offsetWidth || htmlEl.offsetHeight || htmlEl.getClientRects().length);
}

/** querySelectorAll as a real array, optionally filtered to visible elements only. */
export function queryAll<T extends Element = HTMLElement>(selector: string, visibleOnly = false): T[] {
	const elements = Array.from(document.querySelectorAll<T>(selector));
	return visibleOnly ? elements.filter(isVisible) : elements;
}

/** First match for `selector`, or null. */
export function query<T extends Element = HTMLElement>(selector: string): T | null {
	return document.querySelector<T>(selector);
}

/** Last match for `selector`, or null. Optionally require it to be visible. */
export function queryLast<T extends Element = HTMLElement>(selector: string, visibleOnly = false): T | null {
	const elements = queryAll<T>(selector, visibleOnly);
	return elements.length ? elements[elements.length - 1] : null;
}

/** jQuery's `.next(selector)`: the immediately-following sibling, if it matches `selector`. */
export function nextMatching<T extends Element = HTMLElement>(el: Element | null | undefined, selector: string): T | null {
	const sibling = el?.nextElementSibling ?? null;
	return sibling && sibling.matches(selector) ? (sibling as T) : null;
}
